import { useState } from 'react';
import type { ReactNode } from 'react';
import { Agent } from '@/types/entities';
import { EntityDrawer } from '@/components/ui/EntityDrawer';
import { useI18n } from '@/i18n';
import { AGENT_DEPARTMENTS } from '@/config/agentDepartments.config';

export interface AgentFormValues {
  name: string;
  role: string;
  department: string;
  provider: string;
  model: string;
  permissionLevel: 1 | 2 | 3;
  description: string;
  allowedActions: string;
  forbiddenActions: string;
}

const PROVIDERS = ['anthropic', 'openai', 'future_provider', 'custom'];

function toValues(a?: Agent | null): AgentFormValues {
  return {
    name: a?.name ?? '',
    role: a?.role ?? '',
    department: a?.department ?? 'operations',
    provider: a?.provider ?? 'anthropic',
    model: a?.model ?? '',
    permissionLevel: a?.permissionLevel ?? 1,
    description: a?.description ?? '',
    allowedActions: a?.allowedActions ?? '',
    forbiddenActions: a?.forbiddenActions ?? '',
  };
}

export function AgentFormDrawer({ agent, onClose, onSubmit }: { agent?: Agent | null; onClose: () => void; onSubmit: (v: AgentFormValues) => void }) {
  const { t } = useI18n();
  const [values, setValues] = useState<AgentFormValues>(toValues(agent));
  const [error, setError] = useState('');

  function set<K extends keyof AgentFormValues>(key: K, val: AgentFormValues[K]) {
    setValues((v) => ({ ...v, [key]: val }));
  }

  return (
    <EntityDrawer title={agent ? t('agentForm.editTitle') : t('agentForm.newAgent')} onClose={onClose}>
      <Field label={t('agentForm.name')} required><input className="text-input" value={values.name} onChange={(e) => set('name', e.target.value)} /></Field>
      <Field label={t('agentForm.role')}><input className="text-input" value={values.role} onChange={(e) => set('role', e.target.value)} /></Field>
      <Field label={t('agentForm.department')}>
        <select className="text-input" value={values.department} onChange={(e) => set('department', e.target.value)}>
          {AGENT_DEPARTMENTS.map((d) => <option key={d.department} value={d.department}>{t(d.nameKey)}</option>)}
        </select>
      </Field>
      <Field label={t('agentForm.provider')}>
        <select className="text-input" value={values.provider} onChange={(e) => set('provider', e.target.value)}>
          {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </Field>
      <Field label={t('agentForm.model')} required><input className="text-input" value={values.model} onChange={(e) => set('model', e.target.value)} /></Field>
      <Field label={t('agentForm.permissionLevel')}>
        <select className="text-input" value={values.permissionLevel} onChange={(e) => set('permissionLevel', Number(e.target.value) as 1 | 2 | 3)}>
          <option value={1}>{t('permission.level1')}</option>
          <option value={2}>{t('permission.level2')}</option>
          <option value={3}>{t('permission.level3')}</option>
        </select>
      </Field>
      <Field label={t('agentForm.description')}><textarea className="text-input" rows={2} value={values.description} onChange={(e) => set('description', e.target.value)} /></Field>
      <Field label={t('agentForm.allowedActions')}><input className="text-input" value={values.allowedActions} onChange={(e) => set('allowedActions', e.target.value)} /></Field>
      <Field label={t('agentForm.forbiddenActions')}><input className="text-input" value={values.forbiddenActions} onChange={(e) => set('forbiddenActions', e.target.value)} /></Field>

      {error && <div style={{ fontSize: 12, color: 'var(--crit)', marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-primary" onClick={() => (values.name.trim() && values.model.trim() ? onSubmit(values) : setError(t('agentForm.required')))}>{t('common.save')}</button>
        <button onClick={onClose}>{t('common.cancel')}</button>
      </div>
    </EntityDrawer>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>{label}{required && <span style={{ color: 'var(--crit)' }}> *</span>}</label>
      {children}
    </div>
  );
}
